import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { get } from 'lodash'
import classNames from 'classnames'
import Table from 'components/pageComponents/Table'
import EmptyState from 'components/pageComponents/EmptyState'
import LoadingIcon from 'components/atoms/LoadingIcon'
import Switch from 'components/atoms/Switch'
import { IoPencil, IoTrashBin } from 'react-icons/io5'
import notify from 'components/atoms/Notify'
import apiGet from 'lib/network/apiGet'
import apiPut from 'lib/network/apiPut'
import apiDelete from 'lib/network/apiDelete'
import useModal from 'hooks/useModal'
import PageFooter from 'components/paginationComponent'
import AddPointOfInterestModal from './modals/AddPointOfInterestModal'

const PointOfInterestPage = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const [innerTab, setInnerTab] = useState('delivery')
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [pagination, setPagination] = useState({})
  const [search, setSearch] = useState('')
  const [mapUiLang, setMapUiLang] = useState(null)
  const [selectedItemId, setSelectedItemId] = useState(null)

  const fetchData = async (page = 1) => {
    if (page === 1) setLoading(true)
    try {
      const response = await apiGet('points-of-interest', {
        type: innerTab,
        pageNumber: page,
        pageSize: 100,
        search: search || undefined,
      })
      if (response?.data) {
        const rows = response.data.pointsOfInterest || []
        setItems(prev => (page === 1 ? rows : [...prev, ...rows]))
        setPagination(response.data.pagination || {})
      }
    } catch (error) {
      console.error('Error fetching points of interest:', error)
    }
    setLoading(false)
  }

  const handleCloseAirportModal = () => {
    setMapUiLang(null)
    setSelectedItemId(null)
  }

  const openAddAirport = () => {
    setSelectedItemId(null)
    openAirportModal()
  }

  const handleEdit = item => {
    if (innerTab !== 'delivery') return
    setSelectedItemId(item._id)
    openAirportModal()
  }

  const [AirportModal, openAirportModal] = useModal({
    content: <AddPointOfInterestModal onSuccess={() => fetchData(1)} close={handleCloseAirportModal} id={selectedItemId} onMapUiLanguageChange={setMapUiLang} />,
    title:
      mapUiLang === 'ar'
        ? i18n.getFixedT('ar')(selectedItemId ? 'masterData.modals.editAirport' : 'masterData.modals.addAirport')
        : selectedItemId
        ? t('masterData.modals.editAirport')
        : t('masterData.modals.addAirport'),
    dimensions: {
      height: 'auto',
      width: '90vw',
      maxWidth: '680px',
      minHeight: 'auto',
      minWidth: '320px',
      maxHeight: '90vh',
    },
  })

  useEffect(() => {
    fetchData(1)
  }, [innerTab, search])

  const handleDelete = item => {
    if (innerTab !== 'delivery') return
    const itemType = t('masterData.pointOfInterest.airport')
    notify.deleteConfirm({
      title: t('masterData.modals.deleteItem', { itemType }),
      message: t('masterData.modals.deleteConfirm', { itemType }),
      onConfirm: async () => {
        const response = await apiDelete(`point-of-interest/${item._id}`)
        if (response?.success) {
          fetchData(1)
          notify.success(t('masterData.messages.itemDeleted', { itemType }))
        }
      },
    })
  }

  const handleToggle = async (item, checked) => {
    if (innerTab === 'delivery') {
      const response = await apiPut(`point-of-interest/${item._id}`, { active: checked })
      if (response?.success) {
        setItems(prev => prev.map(row => (row._id === item._id ? { ...row, active: checked } : row)))
      }
      return
    }
    const response = await apiPut(`point-of-interest/pickup/${item._id}/active`, { active: checked })
    if (response?.success) {
      setItems(prev => prev.map(row => (row._id === item._id ? { ...row, active: checked } : row)))
    }
  }

  const columns = [
    {
      name: t('masterData.columns.name'),
      key: 'name',
      rendered: item => <div className="text-sm text-gray-900">{item.name}</div>,
    },
    {
      name: t('masterData.columns.arabicName'),
      key: 'nameAr',
      rendered: item => (
        <div className="text-sm text-gray-900" dir="rtl">
          {item.nameAr || ''}
        </div>
      ),
    },
    {
      name: t('masterData.columns.country'),
      key: 'country',
      rendered: item => <div className="text-sm text-gray-900">{isRTL && item.country?.countryAr ? item.country.countryAr : item.country?.country || '-'}</div>,
    },
    ...(innerTab === 'delivery'
      ? [
          {
            name: t('masterData.columns.description'),
            key: 'description',
            rendered: item => <div className="text-sm text-gray-500">{item.description || '-'}</div>,
          },
          {
            name: t('masterData.columns.arabicDescription'),
            key: 'descriptionAr',
            rendered: item => (
              <div className="text-sm text-gray-500" dir="rtl">
                {item.descriptionAr || '-'}
              </div>
            ),
          },
        ]
      : []),
    {
      name: t('masterData.columns.latitude'),
      key: 'lat',
      rendered: item => <div className="text-sm text-gray-900">{item.lat != null ? item.lat : '-'}</div>,
    },
    {
      name: t('masterData.columns.longitude'),
      key: 'lon',
      rendered: item => <div className="text-sm text-gray-900">{item.lon != null ? item.lon : '-'}</div>,
    },
    {
      name: t('masterData.columns.status'),
      key: 'active',
      rendered: item => (
        <div className="flex items-center gap-2">
          <Switch value={item.active !== false} onChange={checked => handleToggle(item, checked)} />
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              item.active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {item.active !== false ? t('masterData.status.active') : t('masterData.status.inactive')}
          </span>
        </div>
      ),
    },
    {
      name: t('masterData.columns.updatedAt'),
      key: 'updated',
      rendered: item => <div className="text-sm text-gray-500">{get(item, 'updatedAt') ? moment(item.updatedAt).fromNow() : 'N/A'}</div>,
    },
    ...(innerTab === 'delivery'
      ? [
          {
            width: '5%',
            name: t('masterData.columns.actions'),
            key: 'actions',
            rendered: item => (
              <div className="flex space-x-4">
                <IoPencil className="text-dark-600 cursor-pointer" onClick={() => handleEdit(item)} />
                <IoTrashBin className="text-red-500 cursor-pointer" onClick={() => handleDelete(item)} />
              </div>
            ),
          },
        ]
      : []),
  ]

  const innerTabs = [
    { id: 'delivery', name: t('masterData.pointOfInterest.deliverToMe') },
    { id: 'pickup', name: t('masterData.pointOfInterest.pickupMyself') },
  ]

  return (
    <div>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-4 border-b border-gray-200 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <nav className={classNames('flex', isRTL ? 'space-x-6 space-x-reverse' : 'space-x-6')}>
          {innerTabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setSearch('')
                setInnerTab(tab.id)
              }}
              className={classNames(
                tab.id === innerTab ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'
              )}
            >
              {tab.name}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            type="text"
            className="block w-56 sm:w-64 pl-3 py-2 sm:text-sm bg-gray-100 border-0 rounded-sm"
            placeholder={`${t('common.search')}...`}
          />
          {innerTab === 'delivery' && (
            <button
              type="button"
              onClick={openAddAirport}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 focus:outline-none"
            >
              {t('masterData.buttons.addAirport')}
            </button>
          )}
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingIcon />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          v2
          description={t('masterData.messages.noDataFound', {
            itemType: innerTab === 'delivery' ? t('masterData.pointOfInterest.airport') : t('masterData.tabs.cities'),
          })}
        />
      ) : (
        <Table
          compact
          hasMore={pagination?.currentPage !== pagination?.totalPages}
          fetchData={() => fetchData((pagination?.currentPage || 1) + 1)}
          data={items}
          columns={columns}
        />
      )}
      <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
      <AirportModal />
    </div>
  )
}

export default PointOfInterestPage
